import { startTransition, useEffect, useReducer, useRef, useState } from "react";
import { trackCtaClicked, trackExperienceOpened } from "@/analytics/tracker";
import { getDemo, nextDemoId, preloadDemo } from "@/demos/registry";
import {
  demoControlReducer,
  initialDemoControls,
  type DemoAction,
  type DemoControlState,
  type DemoId,
} from "@/demos/types";
import { initialDemoFromSearch, replaceDemoInUrl } from "@/journey/navigation";
import { resolvedCtaUrl } from "@/journey/externalNavigation";
import { useDelayedFlag } from "@/journey/useDelayedFlag";
import { useDemoPreloading } from "@/journey/useDemoPreloading";
import { useJourney, type JourneyController } from "@/journey/useJourney";
import { useTransientNotice } from "@/journey/useTransientNotice";
import { reportRuntimeError } from "@/runtime/runtimeErrors";
import { SceneStage } from "@/scene/SceneStage";
import { AssetProgress } from "@/screens/AssetProgress";
import { DemoHud } from "@/screens/DemoHud";
import { EntryScreen } from "@/screens/EntryScreen";
import { FallbackCard } from "@/screens/FallbackCard";
import { PermissionScreen } from "@/screens/PermissionScreen";
import { PlacementOverlay } from "@/screens/PlacementOverlay";
import { PreparingScreen } from "@/screens/PreparingScreen";
import { RuntimeErrorBoundary, RuntimeErrorDetails } from "@/screens/RuntimeErrorBoundary";
import GridScan from "./components/GridScan";


export function App() {
  return (
    <RuntimeErrorBoundary>
      <ExperienceShell />
    </RuntimeErrorBoundary>
  );
}

function ExperienceShell() {
  const [activeDemoId, setActiveDemoId] = useState<DemoId>(() => initialDemoFromSearch(window.location.search));
  const [controls, dispatchControls] = useReducer(demoControlReducer, initialDemoControls);
  const [loadingNext, setLoadingNext] = useState(false);
  const [reducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { notice, showNotice } = useTransientNotice();
  const journey = useJourney({ activeDemoId, showNotice, videoRef });
  const activeDemo = getDemo(activeDemoId);

  // Only escalate to the dedicated Preparing frame if the check is actually
  // slow; otherwise the entry button's busy state is the whole feedback.
  const showPreparing = useDelayedFlag(journey.screen === "preparing", 350);

  useDemoPreloading(activeDemoId, journey.phase);

  useEffect(() => { trackExperienceOpened(activeDemoId); }, [activeDemoId]);

  const goToDemo = async (nextId: DemoId) => {
    if (loadingNext || nextId === activeDemoId) return;
    setLoadingNext(true);
    try {
      await preloadDemo(nextId);
      startTransition(() => {
        setActiveDemoId(nextId);
        replaceDemoInUrl(nextId);
      });
    } catch (cause) {
      reportRuntimeError(cause, "Next experience load failed");
      showNotice("The next experience could not be loaded.");
    } finally {
      setLoadingNext(false);
    }
  };

  const restart = () => {
    dispatchControls({ type: "demo-reset", demo: "engage" });
    void goToDemo("see");
  };

  return (
    <main
      className={`experience-shell mode-${journey.mode} screen-${journey.screen}`}
      data-testid="experience-shell"
    >
      {journey?.mode === "non-ar" ? <GridScan
        className="experience-grid-background"
        linesColor="#413A4C"
        scanColor="#5be7ff"
        cells={8}
        depth={1}
        scanDuration={2.4}
        scanDelay={0.5}
        clickEffect={journey.screen == "entry" ? "ping" : "none"}
        lineThickness={2}
        lineStyle="solid"
        depthFade={0.55}
        shading={0.97}
        reflection={0.35}
        points
        scanDirection="forward"
        scanWidth={1}
        trail={0.7}
        glow={0.6}
        parallax={reducedMotion ? 0 : (journey.screen == "entry" ? 0.25 : 0)}
        autoScan={false}
        interactive
        enableWebcam={false}
        intro={true}
        lightMode={false}
        dpr={1}
      /> : null}
      <div className="ambient-field" aria-hidden="true" />
      <video ref={videoRef} className="camera-feed" muted playsInline aria-hidden="true" />

      <header className="brand-lockup" aria-label="Rising Pixel">
        <img src="assets/images/logo_blue.svg" width={150} />
      </header>

      {journey.webglAvailable ? (
        <SceneStage
          mode={journey.mode}
          placement={journey.placement}
          onPlacement={journey.acceptPlacement}
          onPlacementProgress={journey.setPlacementProgress}
          Scene={activeDemo.Scene}
          arFloorOffsetY={activeDemo.arFloorOffsetY}
          viewerFraming={activeDemo.viewerFraming}
          quality={journey.quality}
          onQualityChange={journey.setQuality}
          reducedMotion={reducedMotion}
          resetEpoch={journey.resetEpoch}
          controls={controls}
          onAction={dispatchControls}
          onWebglContextLost={journey.handleWebglContextLost}
        />
      ) : null}

      {journey.runtimeError ? (
        <aside className="runtime-error-overlay" role="alert" data-testid="runtime-error">
          <div className="runtime-error-card">
            <span className="runtime-error-kicker">Rising Pixel / exception</span>
            <h2>Technical exception</h2>
            <p>{journey.runtimeError.message}</p>
            <span className="runtime-error-context">{journey.runtimeError.context}</span>
            <RuntimeErrorDetails record={journey.runtimeError} />
            <div className="runtime-error-actions">
              <button type="button" onClick={() => journey.setRuntimeError(null)}>Dismiss</button>
              <button type="button" onClick={() => window.location.reload()}>Reload</button>
            </div>
          </div>
        </aside>
      ) : null}

      <div className="hud-layer">
        {journey.webglAvailable ? <AssetProgress /> : null}
        <Screen
          journey={journey}
          activeDemoId={activeDemoId}
          controls={controls}
          notice={notice}
          loadingNext={loadingNext}
          reducedMotion={reducedMotion}
          showPreparing={showPreparing}
          onAction={dispatchControls}
          onSelectDemo={(id) => void goToDemo(id)}
          onNextDemo={() => void goToDemo(nextDemoId(activeDemoId))}
          onRestart={restart}
        />
      </div>
    </main>
  );
}

interface ScreenProps {
  journey: JourneyController;
  activeDemoId: DemoId;
  controls: DemoControlState;
  notice: string | null;
  loadingNext: boolean;
  reducedMotion: boolean;
  showPreparing: boolean;
  onAction: (action: DemoAction) => void;
  onSelectDemo: (id: DemoId) => void;
  onNextDemo: () => void;
  onRestart: () => void;
}

function Screen({
  journey,
  activeDemoId,
  controls,
  notice,
  loadingNext,
  reducedMotion,
  showPreparing,
  onAction,
  onSelectDemo,
  onNextDemo,
  onRestart,
}: ScreenProps) {
  const visit = () => trackCtaClicked(activeDemoId);

  switch (journey.screen) {
    case "unsupported":
      return (
        <FallbackCard
          testId="unsupported"
          title="3D graphics unavailable"
          description="This browser cannot create the graphics context this experience needs. Try a browser with hardware acceleration enabled."
          actions={[]}
        >
          <a className="button-primary compact" href={resolvedCtaUrl()} target="_blank" rel="noopener noreferrer" onClick={visit}>
            Visit Rising Pixel →
          </a>
        </FallbackCard>
      );

    case "entry":
      return <EntryScreen onStart={() => void journey.start()} onVisit={visit} />;

    case "preparing":
      return showPreparing
        ? <PreparingScreen phase={journey.phase} />
        : <EntryScreen busy onStart={() => undefined} onVisit={visit} />;

    case "permission":
      return (
        <PermissionScreen
          access={journey.pendingAccess}
          onAllow={journey.grantAccess}
          onSkip={() => journey.continueIn3D("Continuing in the 3D viewer")}
        />
      );

    case "placement":
      return <PlacementOverlay progress={journey.placementProgress} />;

    case "fallback-no-ar":
      return (
        <FallbackCard
          testId="fallback-no-ar"
          title="AR isn’t available on this device"
          description="AR isn’t available on this device. Explore in 3D instead."
          actions={[{ label: "Continue", primary: true, onSelect: () => journey.continueIn3D() }]}
        />
      );

    case "fallback-permission":
      return (
        <FallbackCard
          testId="fallback-permission"
          title="Camera access not granted"
          description="Continuing without AR placement."
          actions={[
            { label: "Continue", primary: true, onSelect: () => journey.continueIn3D() },
            { label: "Try again", onSelect: () => void journey.retryAccess() },
          ]}
        />
      );

    case "fallback-tracking":
      return (
        <FallbackCard
          testId="fallback-tracking"
          title="Lost tracking"
          description="Move your phone slowly to relocate the surface."
          actions={[
            { label: "Resume", primary: true, onSelect: journey.resumeTracking },
            { label: "Reset", onSelect: journey.reposition },
          ]}
        />
      );

    case "error":
      return (
        <FallbackCard
          testId="journey-error"
          title="Something went wrong"
          description={journey.error ?? "The experience could not start."}
          actions={[
            { label: "Try again", primary: true, onSelect: () => void journey.start() },
            { label: "Continue in 3D", onSelect: () => journey.continueIn3D() },
          ]}
        />
      );

    case "demo":
      return (
        <DemoHud
          activeDemoId={activeDemoId}
          mode={journey.mode}
          controls={controls}
          notice={notice}
          loadingNext={loadingNext}
          reducedMotion={reducedMotion}
          onAction={onAction}
          onReset={() => { onAction({ type: "demo-reset", demo: activeDemoId }); journey.resetDemo(); }}
          onReposition={journey.reposition}
          onSelectDemo={onSelectDemo}
          onNextDemo={onNextDemo}
          onRestart={onRestart}
          onVisit={visit}
        />
      );

    default:
      return null;
  }
}
